import { useEffect, useRef, useState } from 'react'
import styles from './MapTilerTrainingMap.module.css'
import { ITrainingMap } from './TrainingMap'
import L from 'leaflet'
import { MaptilerLayer } from '@maptiler/leaflet-maptilersdk'
import 'leaflet/dist/leaflet.css'

// Fix for default markers in webpack
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

export const MapTilerTrainingMap = ({ coords, isTracker, selectStart, selectEnd, focusedTimeRef }: ITrainingMap) => {
  const mapRef = useRef<L.Map | null>(null)
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const polyActiveRef = useRef<L.Polyline | undefined>(undefined)
  const polyStartRef = useRef<L.Polyline | undefined>(undefined)
  const polyEndRef = useRef<L.Polyline | undefined>(undefined)
  const [mapLoaded, setMapLoaded] = useState<boolean>(false)
  const markerRef = useRef<L.Marker | undefined>(undefined)
  const lastIndex = useRef<number>(0)
  const [controlsVisible, setControlsVisible] = useState<boolean>(false)

  useEffect(() => {
    if (!mapContainerRef.current) return

    const leafletMap = L.map(mapContainerRef.current, {
      zoomControl: false
    }).setView([47, 18], 14)

    const apiKey = process.env.REACT_APP_MAPTILER_API_KEY as string

    new MaptilerLayer({
      apiKey: apiKey,
    }).addTo(leafletMap)

    mapRef.current = leafletMap
    setMapLoaded(true)

    return () => {
      leafletMap.remove()
    }
  }, [])

  useEffect(() => {
    const t = window.setInterval(() => {
      if (!mapRef.current || !coords) return

      let i = Math.floor(focusedTimeRef.current / (isTracker ? 100 : 1000))
      if (lastIndex.current === i) return
      lastIndex.current = i

      try {
        if (markerRef.current) {
          markerRef.current.setLatLng([coords[i].lat, coords[i].lng])
        } else {
          markerRef.current = L.marker([coords[i].lat, coords[i].lng]).addTo(mapRef.current)
        }
      } catch (e) {
        console.log(e)
      }
    }, 200)
    return () => clearInterval(t)
  }, [coords, focusedTimeRef, isTracker])

  const moveMap = (direction: 'up' | 'down' | 'left' | 'right') => {
    if (!mapRef.current) return

    const map = mapRef.current
    const containerSize = map.getSize()

    // Calculate movement distance as a fraction of the container size
    const moveDistance = Math.min(containerSize.x, containerSize.y) * 0.25

    let deltaX = 0
    let deltaY = 0

    switch (direction) {
      case 'up':
        deltaY = -moveDistance
        break
      case 'down':
        deltaY = moveDistance
        break
      case 'left':
        deltaX = -moveDistance
        break
      case 'right':
        deltaX = moveDistance
        break
    }

    map.panBy([deltaX, deltaY])
  }

  const zoomIn = () => {
    if (!mapRef.current) return
    mapRef.current.zoomIn()
  }

  const zoomOut = () => {
    if (!mapRef.current) return
    mapRef.current.zoomOut()
  }

  const toggleControls = () => {
    setControlsVisible(!controlsVisible)
  }

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    try {
      if (polyStartRef.current) map.removeLayer(polyStartRef.current)
      if (polyActiveRef.current) map.removeLayer(polyActiveRef.current)
      if (polyEndRef.current) map.removeLayer(polyEndRef.current)
      if (!coords || coords.length === 0) return

      const startCoords = coords.slice(0, selectStart)
      const activeCoords = coords.slice(selectStart, selectEnd)
      const endCoords = coords.slice(selectEnd)

      if (startCoords.length > 0) {
        const startLatLngs = startCoords.map(coord => [coord.lat, coord.lng] as [number, number])
        polyStartRef.current = L.polyline(startLatLngs, {
          color: '#FF000044',
          weight: 4
        }).addTo(map)
      }

      if (endCoords.length > 0) {
        const endLatLngs = endCoords.map(coord => [coord.lat, coord.lng] as [number, number])
        polyEndRef.current = L.polyline(endLatLngs, {
          color: '#FF000044',
          weight: 4
        }).addTo(map)
      }

      if (activeCoords.length > 0) {
        const activeLatLngs = activeCoords.map(coord => [coord.lat, coord.lng] as [number, number])
        polyActiveRef.current = L.polyline(activeLatLngs, {
          color: '#FF0000',
          weight: 4
        }).addTo(map)

        const group = new L.FeatureGroup([polyActiveRef.current])
        map.fitBounds(group.getBounds(), { padding: [10, 10] })
      } else {
        const allLatLngs = coords.map(coord => [coord.lat, coord.lng] as [number, number])
        const allPolyline = L.polyline(allLatLngs)
        map.fitBounds(allPolyline.getBounds(), { padding: [10, 10] })
      }
    } catch (e) {
      console.log(e)
    }
  }, [mapLoaded, coords, selectStart, selectEnd])

  return (
    <div className={styles.container}>
      <div ref={mapContainerRef} className={styles.map} />
      <button
        className={styles.toggleButton}
        onClick={toggleControls}
        title={controlsVisible ? "Hide controls" : "Show controls"}
      >
        ≡
      </button>
      <div className={`${styles.controls} ${!controlsVisible ? styles.hidden : ''}`}>
        <button
          className={`${styles.controlButton} ${styles.upButton}`}
          onClick={() => moveMap('up')}
          title="Move up"
        >
          ↑
        </button>
        <button
          className={`${styles.controlButton} ${styles.zoomInButton}`}
          onClick={zoomIn}
          title="Zoom in"
        >
          +
        </button>
        <button
          className={`${styles.controlButton} ${styles.leftButton}`}
          onClick={() => moveMap('left')}
          title="Move left"
        >
          ←
        </button>
        <button
          className={`${styles.controlButton} ${styles.rightButton}`}
          onClick={() => moveMap('right')}
          title="Move right"
        >
          →
        </button>
        <button
          className={`${styles.controlButton} ${styles.downButton}`}
          onClick={() => moveMap('down')}
          title="Move down"
        >
          ↓
        </button>
        <button
          className={`${styles.controlButton} ${styles.zoomOutButton}`}
          onClick={zoomOut}
          title="Zoom out"
        >
          -
        </button>
      </div>
    </div>
  )
}
